import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorInfo, UserDetailGroupTaskUser, UserSearchItem } from '../../../../api/model';
import { usePostUsersSearch } from '../../../../api/users/users';
import { useDefaultOptions } from '../../../../hooks/api';
import Modal from '../../../modules/Modal';

// 一覧のソートキー
type SortKey = 'user_id' | 'user_name_ja' | 'user_name_en';

type Props = {
  open: boolean;
  baseUsers: UserDetailGroupTaskUser[];
  userId: number | undefined;
  onClose: () => void;
  add: (params: UserDetailGroupTaskUser) => void;
};

function DisplayUserModal({ open, baseUsers, userId, onClose, add }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  const [selectedUsers, setSelectedUsers] = useState<UserSearchItem[]>([]);

  const { data, mutateAsync } = usePostUsersSearch<AxiosError<ErrorInfo>, unknown>(options);
  const users =
    data?.data.user_lsit_items?.filter(
      (user) => baseUsers.findIndex((u) => u.user_id === user.user_id) === -1 && userId !== user.user_id
    ) || [];

  const [sortInfo] = useState({ key: 'user_name_ja' as SortKey, isDisc: false });
  // 一覧データをソートしたもの
  const sortedUsers = [...users].sort((a, b) => {
    const aAny = a as { [key: string]: string | undefined };
    const bAny = b as { [key: string]: string | undefined };
    const multiValue = sortInfo.isDisc ? -1 : 1;
    if (aAny[sortInfo.key] !== bAny[sortInfo.key]) {
      // 第一ソートキー
      return ((aAny[sortInfo.key] ?? '') > (bAny[sortInfo.key] ?? '') ? 1 : -1) * multiValue;
    }
    return ((a.user_id ?? '') > (b.user_id ?? '') ? 1 : -1) * multiValue;
  });

  const addUser = (user: UserSearchItem) => {
    setSelectedUsers([...selectedUsers, user]);
  };

  const removeUser = (user: UserSearchItem) => {
    setSelectedUsers([...selectedUsers.filter((u) => u.user_id !== user.user_id)]);
  };

  const handleUser = (user: UserSearchItem) => {
    if (selectedUsers.findIndex((u) => u.user_id === user.user_id) > -1) {
      removeUser(user);
    } else {
      addUser(user);
    }
  };

  const handleClose = () => {
    setSelectedUsers([]);
    onClose();
  };

  const handleSubmit = () => {
    selectedUsers.forEach((user) => {
      add({
        user_id: user.user_id,
        user_id_text: user.user_id_text,
        user_name: user.user_name_ja,
        company_name: user.company_name,
        group_name: user.group_name,
        email_address: user.email_address,
        job_number: user.job_number,
        user_chapter: user.chapter_name,
      });
    });
    handleClose();
  };

  const postUsers = useCallback(async () => {
    await mutateAsync({ data: { displaygroup: true } });
  }, [mutateAsync]);

  // 一覧表示
  useEffect(() => {
    void postUsers();
  }, [postUsers]);

  return (
    <Modal active={open} additionalClassName="popupfr900" displayCloseButton onClose={handleClose}>
      <div style={{ height: '400px', overflowY: 'scroll' }}>
        <table className="table41 mb30">
          <tbody>
            <tr>
              <th> </th>
              <th>{t('ユーザー名')}</th>
              <th>{t('ユーザー名(英)')}</th>
              <th>{t('ユーザーID')}</th>
              <th>{t('会社名')}</th>
              <th>{t('グループ名')}</th>
              <th>{t('職番')}</th>
            </tr>
            {sortedUsers.map((user) => (
              <tr key={String(user.user_id)}>
                <td>
                  <span className="ck-box">
                    <input
                      type="checkbox"
                      id={`modal-${String(user.user_id)}`}
                      onChange={() => user.user_id && handleUser(user)}
                    />{' '}
                    <label htmlFor={`modal-${String(user.user_id)}`}>&nbsp;</label>
                  </span>
                </td>
                <td>{user.user_name_ja}</td>
                <td>{user.user_name_en}</td>
                <td>{user.user_id_text}</td>
                <td>{user.company_name}</td>
                <td>{user.group_name}</td>
                <td>{user.job_number}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="controlfr btnfr btnfr1 mb0">
        <div>
          <span className="mr10">
            <input
              type="button"
              value={t('キャンセル').toString()}
              className="btn1 btnbg-g"
              onClick={() => handleClose()}
            />
          </span>
          <span>
            <input type="submit" value={t('選択').toString()} className="btn1 lsp03" onClick={() => handleSubmit()} />
          </span>
        </div>
      </div>
    </Modal>
  );
}

export { DisplayUserModal };
